import React, { useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import Button from '@amzn/meridian/button';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import Sheet from '@amzn/meridian/sheet';
import Text from '@amzn/meridian/text';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { getModificationQueryOptions } from 'src/services/modification/modificationQueries';
import { getTaskMutationOptions, getTaskQueryOptions } from 'src/services/tasks/taskQueries';
import type { RegionTask } from 'src/services/tasks/types';
import { AdminConfigPaths } from 'src/store/adminConfigPageSlice/common/constants';
import {
  fetchRegions,
  resetSeletedRegion,
  setSelectedRegion,
} from 'src/store/adminConfigPageSlice/regionTasksPageSlice/regionTasksPageSlice';
import type { TaskFormTableRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';

import { ROW_HEIGHT } from './constants';
import FormTaskBody from './formTaskBody';
import './formTaskConfiguration.scss';
import { changeTaskOrder, mapDtoToRows } from './taskFormUtils';

import FormTasksTable from './formTasksTable/formTasksTable';
import ModificationEditor from './taskModifications/modificationEditor';

// **** Components **** //

/**
 * Default component
 */
const FormTaskConfiguration = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userLoading } = useAppSelector((state) => state.user);
  const selectedRegion = useAppSelector((state) => state.regionTasksConfigPage.selectedRegion);
  const originalRegion = useAppSelector((state) =>
    state.regionTasksConfigPage.regionTableData.find((r) => r.id === selectedRegion.id),
  );

  // Editable table rows live locally; TanStack Query owns the server data.
  const [taskFormTableData, setTaskFormTableData] = useState<TaskFormTableRow[]>([]);
  const [selectedTaskFormTableRow, setSelectedTaskFormTableRow] = useState<TaskFormTableRow | null>(null);

  // ------------------------------ Data ----------------------------------- //

  const queryClient = useQueryClient();

  const regionTaskGroupsQuery = useQuery({
    ...getTaskQueryOptions.regionTaskGroups(selectedRegion.id),
    enabled: userLoading === 'fulfilled' && !!selectedRegion.id,
  });

  // Warm the modification catalog while the page is up so the editor sheet has
  // it cached on first open, avoiding a spinner-to-content glitch mid-animation.
  useEffect(() => {
    queryClient.prefetchQuery(getModificationQueryOptions.modifications());
  }, [queryClient]);
  const updateRegionTasksMutation = useMutation(getTaskMutationOptions.regionTasks.update());
  const updateRestrictionFormMutation = useMutation(getTaskMutationOptions.restrictionForm.update());

  // Seed local editable rows whenever the server data changes.
  useEffect(() => {
    if (regionTaskGroupsQuery.data) {
      setTaskFormTableData(mapDtoToRows(regionTaskGroupsQuery.data));
    }
  }, [regionTaskGroupsQuery.data]);

  const showSpinner =
    regionTaskGroupsQuery.isFetching || updateRegionTasksMutation.isPending || updateRestrictionFormMutation.isPending;

  // Dirty when the local rows or the edited region diverge from their server baselines.
  const hasChanges = useMemo(() => {
    if (!regionTaskGroupsQuery.data || !originalRegion) return false;
    const baselineRows = mapDtoToRows(regionTaskGroupsQuery.data);
    return !isEqual(taskFormTableData, baselineRows) || !isEqual(selectedRegion, originalRegion);
  }, [taskFormTableData, regionTaskGroupsQuery.data, selectedRegion, originalRegion]);

  // ----------------------- AssignModifications Modal --------------------- //

  // Replace the edited row with the matrix selection from the modal.
  const onModificationsSubmit = (updatedRow: TaskFormTableRow) => {
    setTaskFormTableData((rows) => rows.map((row) => (row.entityId === updatedRow.entityId ? updatedRow : row)));
    setSelectedTaskFormTableRow(null);
  };

  // ------------------------- Form Table --------------------------------- //

  // Change the order of the tasks (via drag-n-drop)
  const updateDataRowOrder = (oldIndex: number, newIndex: number) => {
    if (String(oldIndex) !== String(newIndex)) {
      setTaskFormTableData((rows) => changeTaskOrder(rows, oldIndex, newIndex));
    }
  };

  // ------------------------- Save/Cancel --------------------------------- //

  const onCancel = () => {
    dispatch(resetSeletedRegion());
    navigate(AdminConfigPaths.Form.TABLE, { replace: true });
  };

  const onSave = async () => {
    // Set the rank for the child-tasks to the rank in the parent
    const tasksToUpdate: RegionTask[] = taskFormTableData.flatMap((row) =>
      // Each child-task keeps its own modifications (set per-member in the modal).
      row.type === 'task-group' ? row.tasks.map((childTask) => ({ ...childTask, rank: row.rank })) : row,
    );
    // Call APIs
    await Promise.allSettled([
      updateRestrictionFormMutation.mutateAsync(selectedRegion),
      updateRegionTasksMutation.mutateAsync(tasksToUpdate),
    ]);
    // Refetch regions so the redux baseline realigns with the server and the
    // unsaved-changes banner clears (task rows realign via query invalidation).
    dispatch(fetchRegions());
  };

  // -------------------------- Render DOM --------------------------------- //

  return (
    <div data-testid="form-task-config-panel" className="view-and-edit-forms">
      {/* Form */}
      <LoadingSpinnerWrapper showSpinner={showSpinner} centerOfScreen={true}>
        <Row height={ROW_HEIGHT} alignmentHorizontal="justify" spacingInset="none 300">
          <Button size="small" type="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Row>
            {hasChanges && <Text color="error">*You have unsaved changes</Text>}
            <Button size="small" onClick={onSave}>
              Save
            </Button>
          </Row>
        </Row>
        <Divider spacingAfter="500" />
        <FormTaskBody region={selectedRegion} onUpdate={(region) => dispatch(setSelectedRegion(region))} />
        <Divider spacingBefore="500" spacingAfter="450" />
        <FormTasksTable
          dataRows={taskFormTableData}
          updateDataRowOrder={updateDataRowOrder}
          onClickEdit={(index) => setSelectedTaskFormTableRow(taskFormTableData[index])}
        />
      </LoadingSpinnerWrapper>

      {/* Sheets */}
      <Sheet
        open={selectedTaskFormTableRow != null}
        onClose={() => setSelectedTaskFormTableRow(null)}
        type="overlay"
        side="end"
      >
        <ModificationEditor
          row={selectedTaskFormTableRow}
          onClose={() => setSelectedTaskFormTableRow(null)}
          onSubmit={onModificationsSubmit}
        />
      </Sheet>
    </div>
  );
};

// **** Export **** //

export default FormTaskConfiguration;
